import React, { memo } from 'react';
import { Box, Typography, Switch, IconButton } from '@mui/material';
import { DeleteIcon } from 'icons/materialIcons';
import getIconForType from 'utils/icons/getIconForType';
import { formatPhoneNumber, isPhoneContactType } from 'utils/phoneUtils';

const ContactLinksList = memo(({ contactLinks, onToggleVisibility, onDelete, onEdit, disableEditDelete = false }) => {

  // ✅ FRONTEND-ONLY FIX: Helper function to get visibility state
  const getLinkVisibility = (link) => {
    // Check multiple sources for visibility state
    const isVisibleFromRoot = link.isVisible;
    const isVisibleFromDetails = link.details?.additionalDetails?.isVisible;

    // Priority: root level > additionalDetails > default true
    if (isVisibleFromRoot !== undefined) {
      return isVisibleFromRoot;
    }
    if (isVisibleFromDetails !== undefined) {
      return isVisibleFromDetails;
    }
    return true; // Default to visible
  };

  // Helper function to render Products & Services preview (SIMPLIFIED)
  const renderProductsServicesPreview = (link) => {
    const { details } = link;
    const productTitle = details.productTitle || details.linkTitle || 'Products & Services';

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
        {/* Icon - Show ONLY contact link icon or custom icon, NOT product images */}
        {details.icon ? (
          <Box
            sx={{
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              overflow: 'hidden',
              backgroundImage: `url(${details.icon})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              flexShrink: 0
            }}
          />
        ) : (
          React.cloneElement(getIconForType(link.type), {
            sx: {
              fontSize: '24px',
              color: '#666',
              padding: '4px',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
              flexShrink: 0
            },
          })
        )}

        {/* Product Info - SIMPLIFIED like regular contact links */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.85rem',
              color: '#4f4f4f',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {productTitle}
          </Typography>

          {/* Second line: CTA URL → category → nothing */}
          {details.ctaButton?.link || details.link ? (
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                color: '#828282',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {details.ctaButton?.link || details.link}
            </Typography>
          ) : details.category ? (
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                color: '#828282',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {details.category}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  };

  // Helper function to render regular contact links
  const renderRegularContactLink = (link) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {link.details?.icon ? (
          <Box
            sx={{
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              overflow: 'hidden',
              backgroundImage: `url(${link.details.icon})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              flexShrink: 0
            }}
          />
        ) : (
          React.cloneElement(getIconForType(link.type), {
            sx: {
              fontSize: '24px',
              color: '#666',
              padding: '4px',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
              flexShrink: 0
            },
          })
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.85rem',
              color: '#4f4f4f',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {link.details.linkTitle || link.type}
          </Typography>

          {link.details.linkUrl && link.type !== 'Products & Services' && (
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem',
                color: '#828282',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {(() => {
                if (isPhoneContactType(link.type)) {
                  return formatPhoneNumber(link.details.linkUrl, 'SG');
                }
                return link.details.linkUrl;
              })()}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: "#4f4f4f", mb: 2 }}>
        Saved Links
      </Typography>

      {contactLinks.map((link) => (
        <Box
          key={link._id}
          onClick={() => onEdit && onEdit(link)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
            cursor: onEdit ? 'pointer' : 'default',
            '&:hover': onEdit ? { backgroundColor: '#f5f5f5' } : {},
            '&:not(:last-child)': {
              borderBottom: '1px solid #e0e0e0'
            }
          }}
        >
          {/* Content Area */}
          <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
            {link.type === 'Products & Services'
              ? renderProductsServicesPreview(link)
              : renderRegularContactLink(link)
            }
          </Box>

          {/* Controls */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ✅ FRONTEND-ONLY FIX: Use getLinkVisibility function */}
            <Switch
              size="small"
              checked={getLinkVisibility(link)}
              onChange={() => onToggleVisibility(link._id, !getLinkVisibility(link))}
              sx={{ mr: 1 }}
            />
            {!disableEditDelete && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit && onEdit(link);
                  }}
                  sx={{ color: '#666' }}
                >
                  {/* Assuming EditIcon is available or needs to be imported */}
                  {/* For now, using a placeholder or removing if not needed */}
                  {/* <EditIcon /> */}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete && onDelete(link._id);
                  }}
                  sx={{ color: '#666' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      ))}

      {contactLinks.length === 0 && (
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.8rem',
            color: '#828282',
            fontStyle: 'italic',
            textAlign: 'center',
            py: 2
          }}
        >
          No links added yet
        </Typography>
      )}
    </Box>
  );
});

export default ContactLinksList;